/**
 * Pure helpers for well marker multi-select on the acreage map.
 */

/**
 * Toggle a well key in the selected set.
 *
 * - Plain click: select only this well; if it was already the sole selection, clear.
 * - multi (Shift / Cmd / Ctrl): add or remove without clearing others.
 *
 * @param {string[]} selectedKeys
 * @param {string} key
 * @param {{ multi?: boolean }} [opts]
 * @returns {string[]}
 */
export function toggleWellSelection(selectedKeys, key, { multi = false } = {}) {
  if (!key) return Array.isArray(selectedKeys) ? [...selectedKeys] : [];
  const prev = Array.isArray(selectedKeys) ? selectedKeys : [];
  if (multi) {
    const set = new Set(prev);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    return [...set];
  }
  if (prev.length === 1 && prev[0] === key) return [];
  return [key];
}

/**
 * True when the DOM click event requests multi-select (Shift / Cmd / Ctrl).
 * @param {MouseEvent | undefined | null} originalEvent
 */
export function isMultiSelectModifier(originalEvent) {
  if (!originalEvent) return false;
  return Boolean(originalEvent.shiftKey || originalEvent.metaKey || originalEvent.ctrlKey);
}

/**
 * Resolve the first selected well key against one or more well lists.
 * Prefer earlier pools (e.g. visible markers) then fall back to a full directory
 * so Open still works when filters hide the selected marker.
 *
 * @param {string[]} selectedKeys
 * @param {Array<Array<{ api?: string } | null | undefined> | null | undefined>} wellPools
 * @param {(w: { api?: string }) => string} keyFn
 * @returns {{ api?: string } | null}
 */
export function findFirstSelectedWell(selectedKeys, wellPools, keyFn) {
  if (!selectedKeys?.length || typeof keyFn !== 'function') return null;
  const key = selectedKeys[0];
  if (!key) return null;
  for (const pool of wellPools || []) {
    if (!Array.isArray(pool)) continue;
    const hit = pool.find((w) => w && keyFn(w) === key);
    if (hit) return hit;
  }
  return null;
}
